import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Alert } from '@neo/primitives/alert'
import { Input } from '@neo/primitives/input'
import { Separator } from '@neo/primitives/separator'
import type { ApiResponse, LoginFormData } from '@shared/types'
import dayjs from 'dayjs'
import React, { useEffect, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, Navigate, useNavigate, useSearchParams } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { queryClient } from 'v2/configs/rq-setup'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import type { RLoginResponse } from 'v2/types/api/auth'
import { getSafeRedirectUrl } from 'v2/utils/url-validation'
import { z } from 'zod'
import { AuthLayout } from '../../shared/layouts/auth'
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from './components/auth-styles'
import { SsoProviderButtons, type SsoProviderId } from './components/sso-provider-buttons'
import { useLoginMutationV2, useSsoAuthorizeMutation } from './queries'
import { getLoginErrorText } from './utils/login-errors'

type LoginResponse = ApiResponse<RLoginResponse>

// Constants for magic values
const OTP_METHOD = {
  SMS: 'sms',
} as const

// Brokered/SAML SSO routed by email domain (WorkOS). Intentionally not a SsoProviderId — it has no
// global provider button; it's the email-based "Sign in with SSO" path.
const WORKOS_SSO_TYPE = 'workos' as const

const AUTH_TYPE = {
  LOGIN: false,
  SIGNUP: true,
} as const

// Helper function to create base navigation state
const createBaseNavigationState = (
  formData: LoginFormData,
  response: LoginResponse,
  nextUrl: string | null,
) => ({
  email: formData.email,
  password: formData.password,
  accountID: response.data.account_id,
  aomUUID: response.data.aom_uuid,
  isSignup: AUTH_TYPE.LOGIN,
  originalLoginResponse: response.data,
  region: response.data.region,
  nextUrl,
})

// Helper function to create two-factor verify navigation state
const createTwoFactorVerifyState = (
  formData: LoginFormData,
  response: LoginResponse,
  nextUrl: string | null,
) => ({
  ...createBaseNavigationState(formData, response, nextUrl),
  phoneNumber: response.data.phone_number,
  sessionUuid: response.data.session_uuid,
  method: OTP_METHOD.SMS,
  isAuthenticatorDefault: response.data.authenticator_status.is_default,
  isAuthenticatorEnabled: response.data.authenticator_status.is_present,
  otpSentTime: dayjs().toISOString(),
})

// Helper function to create two-factor auth navigation state
const createTwoFactorAuthState = (
  formData: LoginFormData,
  response: LoginResponse,
  nextUrl: string | null,
) => ({
  ...createBaseNavigationState(formData, response, nextUrl),
  verificationPending: true,
})

const fields: GenericFieldConfig<LoginFormData> = [
  {
    name: 'email',
    componentType: 'input',
    zodValidation: z
      .string()
      .min(1, 'Email is required')
      .email('Invalid email')
      .max(75, 'Email cannot exceed 75 characters'),
    label: 'Work Email',
    inputProps: {
      placeholder: 'jane@company.com',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'username',
      'data-testid': 'input-email',
    },
  },
  {
    name: 'password',
    componentType: 'inputPassword',
    zodValidation: z.string().min(1, 'Password is required'),
    label: (
      <div className="flex items-center justify-between gap-2">
        <span>Password</span>
        <Link to="/forgot-password" className={`${AUTH_LINK_CLASS} text-xs-400`}>
          Forgot your password?
        </Link>
      </div>
    ),
    inputProps: {
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'current-password',
      placeholder: 'Enter your password',
      'data-testid': 'input-password',
    },
  },
]

export const LoginV2 = () => {
  const recaptchaRef = React.useRef() as React.MutableRefObject<ReCAPTCHA>
  const { mutate: loginUser, isPending, error } = useLoginMutationV2(recaptchaRef)
  const ssoMutation = useSsoAuthorizeMutation()
  const { userAuthenticated } = useAuth()
  const defaultValueRef = useRef<LoginFormData>({ email: '', password: '' })
  const [searchParams, setSearchParams] = useSearchParams()
  const nextUrl = getSafeRedirectUrl(searchParams.get('next_url'))
  // Carry next_url into signup. Someone sent here by `plivo login` who does not
  // yet have an account used to land on a bare /signup, finish, and arrive at
  // the dashboard — while the CLI sat waiting and timed out. Reuses the
  // already-sanitised nextUrl so signup cannot be pointed off-site.
  const signupHref = nextUrl ? `/signup?next_url=${encodeURIComponent(nextUrl)}` : '/signup'
  const [ssoError, setSsoError] = React.useState<string | null>(() => searchParams.get('sso_error'))
  // Email-based SSO (e.g. WorkOS/SAML): connection is resolved by email domain, so unlike the
  // global provider buttons this path needs an email before redirecting.
  const [showSsoEmail, setShowSsoEmail] = React.useState(false)
  const [ssoEmail, setSsoEmail] = React.useState('')
  const normalizedSsoEmail = ssoEmail.trim()
  const isSsoEmailValid = z.string().email().safeParse(normalizedSsoEmail).success
  const navigate = useNavigate()

  useEffect(() => {
    if (searchParams.has('sso_error')) {
      setSsoError(searchParams.get('sso_error'))
      setSearchParams(
        (prev) => {
          prev.delete('sso_error')
          return prev
        },
        { replace: true },
      )
    }
  }, [])

  useEffect(() => {
    if (!userAuthenticated) {
      queryClient.removeQueries()
      queryClient.clear()
    }
  }, [userAuthenticated])

  // If the user is already authenticated, redirect them to the inbox.
  if (userAuthenticated) {
    if (nextUrl) {
      return <Navigate to={nextUrl} />
    }

    return <Navigate to={getDefaultRoute()} />
  }

  // Navigate to two-factor verification screen for login
  const navigateToTwoFactorVerify = (formData: LoginFormData, response: LoginResponse) => {
    const navigationState = createTwoFactorVerifyState(formData, response, nextUrl)
    navigate('/two-factor-verify', { state: navigationState })
  }

  // Navigate to two-factor auth screen
  const navigateToTwoFactorAuth = (formData: LoginFormData, response: LoginResponse) => {
    const navigationState = createTwoFactorAuthState(formData, response, nextUrl)
    navigate('/two-factor-auth', { state: navigationState })
  }

  const handleLogin = (formData: LoginFormData) => {
    loginUser(formData, {
      onSuccess: (response) => {
        // Check if 2FA is required
        if (response.data.two_fa_enabled && !response.data.redirect_two_fa_auth) {
          navigateToTwoFactorVerify(formData, response)
        } else if (response.data.two_fa_enabled && response.data.redirect_two_fa_auth) {
          navigateToTwoFactorAuth(formData, response)
        }
        // If no 2FA required, the normal login flow will proceed automatically
        // The login mutation will handle authentication and navigation
      },
    })
  }

  const errorText = getLoginErrorText(error, ssoError)

  // Dedicated full-screen SSO step (mirrors phlo-console's /sso/auth screen): clicking
  // "Login with SSO" replaces the whole login screen with just a work-email input.
  if (showSsoEmail) {
    return (
      <AuthLayout
        pageTitle="Login with SSO"
        title="Login with SSO"
        description="Enter your work email and we'll take you to your identity provider."
        footer={
          <div className="text-p-400 text-secondary text-center">
            <Button
              variant="link"
              onClick={() => setShowSsoEmail(false)}
              data-testid="login-sso-back-btn"
            >
              Back to login
            </Button>
          </div>
        }
      >
        {ssoError && <Alert variant="destructive" content={ssoError} />}
        <Input
          inputClassName={AUTH_INPUT_CLASS}
          type="email"
          placeholder="jane@company.com"
          value={ssoEmail}
          onChange={(e) => setSsoEmail(e.target.value)}
          data-testid="login-sso-email-input"
        />
        <Button
          typography="action"
          className={AUTH_PRIMARY_BUTTON_CLASS}
          size="xl"
          disabled={!isSsoEmailValid || ssoMutation.isPending}
          disabledReason={!isSsoEmailValid ? 'Enter a valid work email to continue.' : undefined}
          onClick={() =>
            ssoMutation.mutate({
              sso_type: WORKOS_SSO_TYPE,
              email: normalizedSsoEmail,
              next: nextUrl || undefined,
            })
          }
          data-testid="login-sso-continue-btn"
        >
          {ssoMutation.isPending && ssoMutation.variables?.sso_type === WORKOS_SSO_TYPE
            ? 'Redirecting…'
            : 'Continue'}
        </Button>
      </AuthLayout>
    )
  }

  return (
    <div>
      <AuthLayout
        pageTitle="Login to your account"
        title="Login to your account"
        description="Welcome back! Manage and monitor your AI agents."
        footer={
          <div className="text-p-400 text-secondary text-center grid gap-2">
            <div>
              Don&apos;t have an account?{' '}
              <Link to={signupHref} className={AUTH_LINK_CLASS} data-testid="login-signup-link">
                Sign up
              </Link>
            </div>
            <div>
              Didn&apos;t receive the activation email?{' '}
              <Link
                to="/resend-activation-email"
                className={AUTH_LINK_CLASS}
                data-testid="link-login-resend-activation"
              >
                Resend email
              </Link>
            </div>
          </div>
        }
      >
        {errorText && <Alert variant="destructive" content={errorText} />}
        <GenericForm
          id="login-form"
          defaultValues={defaultValueRef.current}
          fields={fields}
          onSubmit={handleLogin}
          className="grid gap-4"
          isSubmitting={isPending}
          submitText="Login"
          includeSubmitButton={false}
        />
        <div className="flex flex-col gap-4">
          <FormSubmitButton
            formId="login-form"
            typography="action"
            className="w-full rounded-sm disabled:bg-st1 disabled:text-secondary disabled:opacity-100"
            size="xl"
            loading={isPending}
          >
            Login
          </FormSubmitButton>
        </div>
        <div className="flex items-center gap-4">
          <Separator className="flex-1" />
          <span className="font-jetbrains text-label uppercase text-tertiary">OR</span>
          <Separator className="flex-1" />
        </div>
        <SsoProviderButtons
          isPending={ssoMutation.isPending}
          pendingProvider={ssoMutation.variables?.sso_type as SsoProviderId | undefined}
          onSelect={(ssoType) =>
            ssoMutation.mutate({ sso_type: ssoType, next: nextUrl || undefined })
          }
        />
        <Button
          variant="outline"
          typography="action"
          className="h-12 w-full border-st2 hover:border-brand hover:bg-background hover:text-primary after:hidden"
          onClick={() => setShowSsoEmail(true)}
          data-testid="login-sso-toggle-btn"
        >
          Login with SSO
        </Button>
        <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-center sm:flex-nowrap">
          <span className="text-xs-400 text-secondary">
            By continuing, you agree to Plivo&apos;s
          </span>
          <Link
            to="https://www.plivo.com/legal/tos/"
            className={`${AUTH_LINK_CLASS} text-xs-400`}
            data-testid="link-terms-of-service"
            target="_blank"
            rel="noopener"
          >
            ToS
          </Link>
          <span className="text-xs-400 text-secondary">&</span>
          <Link
            to="https://www.plivo.com/legal/privacy/"
            className={`${AUTH_LINK_CLASS} text-xs-400`}
            data-testid="link-privacy-policy"
            target="_blank"
            rel="noopener"
          >
            Privacy Policy
          </Link>
        </div>
        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
      </AuthLayout>
    </div>
  )
}
